import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, authFetch } from "../api/client.ts";
import { useAuthStore } from "../store/useAuthStore.ts";
import { NoReviewNotice } from "../components/NoReviewNotice.tsx";
import { Pagination } from "../components/Pagination.tsx";
import { formatUsageWithCost, useCostDisplay } from "../lib/cost.ts";
import {
  Loader2, CheckCircle2, XCircle, HelpCircle, Bot,
  ChevronLeft, ChevronRight, ExternalLink, FileText,
  ArrowRight, Info, Unlink, UserCheck, LayoutList, LayoutGrid, Ban,
  AlertTriangle, Scale, ClipboardList, ShieldCheck, Check, X, MessageSquareCode, Copy, RotateCcw,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReviewOption {
  id: string;
  name: string;
  type: string;
  // The review's numbered criteria, same ids as the AI table (I1.., E1..)
  criteria?: Array<{ id: string; kind: "INCLUSION" | "EXCLUSION"; criterion: string }>;
  inclusionCategories?: string[];
  exclusionCategories?: string[];
}

interface ConflictDecision {
  studyId: string;
  phase: string;
  decision: string;
  reviewerId: string;
  inclusionReason: string | null;
  inclusionReasonCodes?: string[];
  exclusionReason: string | null;
  exclusionReasonCodes?: string[];
  uncertainReason?: string | null;
  reviewer: { id: string; name: string };
}

interface ConflictItem {
  studyId: string;
  phase: string;
  reason?: "DISAGREEMENT" | "UNCERTAIN_AT_FULL_TEXT";
  decisions: ConflictDecision[];
  study: {
    id: string;
    title: string;
    abstract: string | null;
    authors: string[];
    year: number | null;
    journal: string | null;
    doi: string | null;
  } | null;
}

interface StudyItem {
  id: string;
  title: string;
  abstract: string | null;
  authors: string[];
  year: number | null;
  journal: string | null;
  doi: string | null;
  fullTextUrl: string | null;
  fullTextPath: string | null;
  status: string;
  screeningDecisions: Array<{
    reviewerId: string;
    decision: string;
    isAiSuggested: boolean;
    aiConfidenceScore: number | null;
    aiRawResponse: string | null;
    inclusionReason: string | null;
    uncertainReason?: string | null;
    inclusionReasonCodes?: string[];
    exclusionReason: string | null;
    exclusionReasonCodes?: string[];
  }>;
  // Only present in FULL_TEXT phase — prior abstract-based AI suggestion for context
  titleAbstractAiDecision?: {
    decision: string;
    aiConfidenceScore: number | null;
    aiRawResponse: string | null;
  } | null;
}

interface UnallocatedStudy {
  id: string;
  title: string;
  abstract: string | null;
  authors: string[];
  year: number | null;
  journal: string | null;
  doi: string | null;
  source: string;
  screeningDecisions: Array<{
    decision: string;
    aiConfidenceScore: number | null;
    aiRawResponse: string | null;
    review: { id: string; name: string; type: string };
  }>;
}

type Verdict = "YES" | "NO" | "UNCLEAR";

// One row of the AI's per-criterion assessment. INCLUSION: YES = met.
// EXCLUSION: YES = the exclusion applies.
interface CriterionRow {
  id: string;
  kind: "INCLUSION" | "EXCLUSION";
  criterion: string;
  label: string;
  verdict: Verdict | null; // null: the AI has not assessed this criterion
  note: string;
}

/**
 * The table always lists the review's own criteria; AI verdicts are merged in
 * where an AI row has the same id and wording. So the reviewer can mark every
 * criterion even when no AI suggestion exists, or the criteria changed since.
 */
function mergeCriteria(
  reviewCriteria: Array<{ id: string; kind: "INCLUSION" | "EXCLUSION"; criterion: string }>,
  aiRows: CriterionRow[],
): CriterionRow[] {
  if (reviewCriteria.length === 0) return aiRows;
  const byId = new Map(aiRows.map((r) => [r.id, r]));
  return reviewCriteria.map((c) => {
    const ai = byId.get(c.id);
    const matches = ai && ai.kind === c.kind && ai.criterion.trim() === c.criterion.trim();
    return { ...c, label: c.criterion, verdict: matches ? ai.verdict : null, note: matches ? ai.note : "" };
  });
}

/** What the AI card renders, whether from a fresh call or a stored suggestion. */
interface AiView {
  decision: "INCLUDE" | "EXCLUDE" | "UNCERTAIN";
  confidence: number | null;
  highlights: string[];
  criteria: CriterionRow[];
  summary: string;
}

function storedAiView(stored: { decision: string; aiConfidenceScore: number | null; aiRawResponse: string | null }): AiView {
  let p: { reasoning?: string; keyFactors?: string[]; criteria?: CriterionRow[] } = {};
  try { p = JSON.parse(stored.aiRawResponse ?? "{}") as typeof p; } catch { /* ignore */ }
  return {
    decision: stored.decision as AiView["decision"],
    confidence: stored.aiConfidenceScore,
    highlights: p.keyFactors ?? [],
    criteria: Array.isArray(p.criteria) ? p.criteria : [],
    summary: p.reasoning ?? "",
  };
}

interface AiPrediction {
  suggestion: {
    decision: "INCLUDE" | "EXCLUDE" | "UNCERTAIN";
    confidence: number;
    reasoning: string;
    keyFactors: string[];
    highlights?: string[];
    criteria?: CriterionRow[];
    summary?: string;
    charsAvailable: number | null;
    charsUsed: number | null;
  };
  textSource: "full_text" | "abstract" | "title_only" | "scanned_pdf";
  aiMaxChars: number | null;
  cached: boolean;
  warning: string;
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ScreeningPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlReviewId = searchParams.get("reviewId") ?? "";
  const urlMode = searchParams.get("mode") ?? "screening";

  const { user } = useAuthStore();
  const isCoordinator = user?.role === "COORDINATOR" || user?.role === "ADMIN";

  const { data: reviewsData } = useQuery({
    queryKey: ["reviews-for-screening"],
    // /screening/my-reviews, not /admin/reviews: the latter is ADMIN/COORDINATOR/
    // SUB_TEAM_LEADER-only and unfiltered, which would 403 for a REVIEWER (the
    // primary audience of this page) and would otherwise leak every review's
    // existence to reviewers who aren't assigned to all of them.
    queryFn: () => api.get<{ reviews: ReviewOption[] }>("/screening/my-reviews"),
  });
  const reviews = reviewsData?.reviews ?? [];

  const [reviewId, setReviewId] = useState<string>(() => urlReviewId);
  const effectiveReviewId = reviewId || (reviews.length > 0 ? reviews[0]!.id : "");

  const setMode = (m: string) => setSearchParams((p) => { p.set("mode", m); return p; });

  // Loaded and nothing to show: say why instead of the "Loading reviews"
  // spinner, which previously span forever for anyone not yet on a review.
  if (reviewsData && reviews.length === 0) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Screening</h1>
        {isCoordinator
          ? <p className="text-sm text-muted-foreground">There are no active reviews yet. Create one in Admin, Reviews.</p>
          : <NoReviewNotice />}
      </div>
    );
  }

  // Papers in no review yet are a coordinator's triage job; the backend
  // refuses this list to anyone else too.
  if (urlMode === "unallocated" && isCoordinator) {
    return (
      <div>
        {isCoordinator && <ModeTabBar mode="unallocated" onSetMode={setMode} />}
        <UnallocatedPanel reviews={reviews} />
      </div>
    );
  }

  if (urlMode === "conflicts" && isCoordinator) {
    return (
      <div>
        <ModeTabBar mode="conflicts" onSetMode={setMode} />
        <ConflictsPanel reviews={reviews} effectiveReviewId={effectiveReviewId} onReviewChange={setReviewId} />
      </div>
    );
  }

  return (
    <div>
      {isCoordinator && <ModeTabBar mode="screening" onSetMode={setMode} />}
      <ScreeningQueue
        reviewId={effectiveReviewId}
        reviews={reviews}
        onReviewChange={(id) => { setReviewId(id); }}
      />
    </div>
  );
}

function ModeTabBar({ mode, onSetMode }: { mode: string; onSetMode: (m: string) => void }) {
  return (
    <div className="flex gap-1 px-6 pt-4 border-b bg-background">
      <button
        onClick={() => onSetMode("screening")}
        className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${mode === "screening" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
      >
        Screening queue
      </button>
      <button
        onClick={() => onSetMode("conflicts")}
        className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${mode === "conflicts" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
      >
        <AlertTriangle className="h-3.5 w-3.5" /> Conflicts
      </button>
      <button
        onClick={() => onSetMode("unallocated")}
        className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${mode === "unallocated" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
      >
        <Unlink className="h-3.5 w-3.5" /> Unallocated
      </button>
    </div>
  );
}

// ─── Conflicts panel ─────────────────────────────────────────────────────────

function ConflictsPanel({
  reviews,
  effectiveReviewId,
  onReviewChange,
}: {
  reviews: ReviewOption[];
  effectiveReviewId: string;
  onReviewChange: (id: string) => void;
}) {
  const [reviewId, setReviewId] = useState(effectiveReviewId);
  const [resolving, setResolving] = useState<{ studyId: string; phase: string } | null>(null);
  const [decision, setDecision] = useState<"INCLUDE" | "EXCLUDE" | null>(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const effectiveId = reviewId || effectiveReviewId;

  const { data, isLoading } = useQuery({
    queryKey: ["conflicts", effectiveId],
    queryFn: () => api.get<{ conflicts: ConflictItem[] }>(`/screening/conflicts?reviewId=${effectiveId}`),
    enabled: !!effectiveId,
  });
  const conflicts = data?.conflicts ?? [];

  const startResolve = (studyId: string, phase: string) => {
    setResolving({ studyId, phase });
    setDecision(null);
    setReason("");
    setError(null);
  };

  const submitResolution = async () => {
    if (!resolving || !decision) return;
    if (!reason.trim()) { setError("A resolution reason is required."); return; }
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/screening/coordinator-decision", {
        studyId: resolving.studyId,
        reviewId: effectiveId,
        phase: resolving.phase,
        decision,
        reason: reason.trim(),
      });
      setResolving(null);
      setDecision(null);
      setReason("");
      void qc.invalidateQueries({ queryKey: ["conflicts"] });
      void qc.invalidateQueries({ queryKey: ["review-stats"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Resolution failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Scale className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-2xl font-bold">Conflicts</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        These studies received conflicting decisions from different reviewers. As coordinator or admin,
        you make the final call. Your decision overrides the disagreement and advances the study.
      </p>

      {/* Review selector */}
      <div className="mb-5">
        <label className="block text-xs font-medium text-muted-foreground mb-1">Review</label>
        <select
          value={effectiveId}
          onChange={(e) => { setReviewId(e.target.value); onReviewChange(e.target.value); setResolving(null); }}
          className="border rounded-md px-3 py-1.5 text-sm bg-background"
        >
          {reviews.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading conflicts…
        </div>
      )}

      {!isLoading && conflicts.length === 0 && (
        <div className="text-center py-16 border rounded-lg bg-card text-muted-foreground">
          <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
          <p className="font-semibold text-base text-foreground">No conflicts for this review</p>
          <p className="text-sm mt-1">All screening decisions are unanimous.</p>
        </div>
      )}

      <div className="space-y-4">
        {conflicts.map((c) => {
          const isResolving = resolving?.studyId === c.studyId && resolving?.phase === c.phase;
          const study = c.study;
          return (
            <div key={`${c.studyId}:${c.phase}`} className="border rounded-lg bg-card overflow-hidden">
              {/* Study header */}
              <div className="px-5 py-4 border-b">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.phase === "TITLE_ABSTRACT" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"}`}>
                        {c.phase === "TITLE_ABSTRACT" ? "Title/Abstract" : "Full Text"}
                      </span>
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                      <span className="text-xs text-amber-700 font-medium">
                        {c.reason === "UNCERTAIN_AT_FULL_TEXT"
                          ? "Uncertain at full text: a reviewer could not decide from the full paper"
                          : "Reviewers disagree: one included, another excluded"}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold">{study?.title ?? c.studyId}</h3>
                    {study && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {study.authors.slice(0, 3).join(", ")}
                        {study.authors.length > 3 ? " et al." : ""}
                        {study.year ? ` (${study.year})` : ""}
                        {study.journal ? `, ${study.journal}` : ""}
                      </p>
                    )}
                    {study?.doi && (
                      <a
                        href={`https://doi.org/${study.doi.replace(/^https?:\/\/doi\.org\//i, "")}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-primary hover:underline mt-1 w-fit"
                      >
                        <ExternalLink className="h-3 w-3" /> {study.doi}
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Abstract */}
              {study?.abstract && (
                <div className="px-5 py-3 border-b bg-secondary/20">
                  <p className="text-xs leading-relaxed text-muted-foreground line-clamp-4">{study.abstract}</p>
                </div>
              )}

              {/* Reviewer decisions side by side */}
              <div className="grid grid-cols-2 divide-x border-b">
                {c.decisions.map((d) => (
                  <div key={d.reviewerId} className="px-4 py-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">{d.reviewer.name}</p>
                    <DecisionBadge decision={d.decision as "INCLUDE" | "EXCLUDE" | "UNCERTAIN"} />
                    {d.inclusionReason && (
                      <p className="text-xs text-muted-foreground mt-1.5 italic">"{d.inclusionReason}"</p>
                    )}
                    <CriteriaBadges codes={d.inclusionReasonCodes} />
                    {d.exclusionReason && (
                      <p className="text-xs text-muted-foreground mt-1.5 italic">"{d.exclusionReason}"</p>
                    )}
                    <CriteriaBadges codes={d.exclusionReasonCodes} />
                    {d.uncertainReason && (
                      <p className="text-xs text-muted-foreground mt-1.5 italic">"{d.uncertainReason}"</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Resolution panel */}
              {isResolving ? (
                <div className="px-5 py-4 bg-primary/5 border-t space-y-3">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Scale className="h-4 w-4" /> Coordinator decision
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setDecision("INCLUDE")}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border-2 transition-colors ${decision === "INCLUDE" ? "bg-green-500 text-white border-green-500" : "border-green-200 text-green-700 hover:bg-green-50"}`}
                    >
                      <CheckCircle2 className="h-4 w-4" /> Include
                    </button>
                    <button
                      onClick={() => setDecision("EXCLUDE")}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border-2 transition-colors ${decision === "EXCLUDE" ? "bg-red-500 text-white border-red-500" : "border-red-200 text-red-700 hover:bg-red-50"}`}
                    >
                      <XCircle className="h-4 w-4" /> Exclude
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Resolution reason (required)</label>
                    <input
                      type="text"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && void submitResolution()}
                      placeholder="e.g. Reviewed full text, meets all inclusion criteria despite abstract ambiguity"
                      className="w-full px-3 py-2 border rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                      autoFocus
                    />
                  </div>
                  {error && <p className="text-xs text-destructive">{error}</p>}
                  <div className="flex gap-2">
                    <button
                      onClick={() => void submitResolution()}
                      disabled={submitting || !decision}
                      className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                    >
                      {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      Confirm resolution
                    </button>
                    <button
                      onClick={() => setResolving(null)}
                      className="px-4 py-2 rounded-md border text-sm font-medium hover:bg-accent"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-5 py-3 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Reviewers disagreed. Your decision is final and will advance the study.
                  </span>
                  <button
                    onClick={() => startResolve(c.studyId, c.phase)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                  >
                    <Scale className="h-3.5 w-3.5" /> Resolve
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Unallocated triage panel ─────────────────────────────────────────────────

function UnallocatedPanel({ reviews }: { reviews: ReviewOption[] }) {
  const [index, setIndex] = useState(0);
  const [assigning, setAssigning] = useState<string | null>(null); // studyId being assigned
  const [confirming, setConfirming] = useState<string | null>(null); // studyId being confirmed excluded
  const [viewMode, setViewMode] = useState<"card" | "list">("list");
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["unallocated-studies"],
    queryFn: () => api.get<{ studies: UnallocatedStudy[]; total: number }>("/studies/unallocated"),
  });

  const studies = data?.studies ?? [];
  const current = studies[index];

  const assignMutation = useMutation({
    mutationFn: ({ studyId, reviewId }: { studyId: string; reviewId: string }) =>
      api.post(`/studies/${studyId}/assign-review`, { reviewId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["unallocated-studies"] });
      void qc.invalidateQueries({ queryKey: ["study-stats"] });
      void qc.invalidateQueries({ queryKey: ["review-stats"] });
      setIndex((i) => Math.min(i, studies.length - 2));
      setAssigning(null);
    },
  });

  // Confirms the AI exclusion decision: sets study status to EXCLUDED.
  // Creates an audit record; the AI reasoning stored in ScreeningDecision serves as justification.
  const confirmExcludedMutation = useMutation({
    mutationFn: (studyId: string) =>
      api.patch(`/studies/${studyId}`, { status: "EXCLUDED" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["unallocated-studies"] });
      void qc.invalidateQueries({ queryKey: ["study-stats"] });
      void qc.invalidateQueries({ queryKey: ["review-stats"] });
      setIndex((i) => Math.min(i, studies.length - 2));
      setConfirming(null);
    },
  });

  const nav = (dir: -1 | 1) => setIndex((i) => Math.min(Math.max(0, i + dir), studies.length - 1));

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Unlink className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-2xl font-bold">Unallocated Studies</h1>
        {studies.length > 0 && (
          <span className="ml-auto flex items-center gap-1 border rounded-md overflow-hidden text-xs">
            <button
              onClick={() => setViewMode("card")}
              title="Card view: one study at a time"
              className={`flex items-center gap-1.5 px-2.5 py-1.5 ${viewMode === "card" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Card
            </button>
            <button
              onClick={() => setViewMode("list")}
              title="List view: all studies at once"
              className={`flex items-center gap-1.5 px-2.5 py-1.5 border-l ${viewMode === "list" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
            >
              <LayoutList className="h-3.5 w-3.5" /> Show all
            </button>
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        These papers were not automatically assigned to any review. The AI predicted EXCLUDE across
        all active reviews, or screening is still processing. For each paper: either{" "}
        <strong className="text-foreground">assign it to a review</strong> (if you disagree with the AI)
        or{" "}
        <strong className="text-foreground">confirm the exclusion</strong> to create a human-verified
        exclusion record without adding it to a review queue.
      </p>

      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-5 p-3 bg-secondary/50 rounded-lg">
        <PhaseStep label="Triage · Unallocated" active />
        <ArrowRight className="h-3 w-3 shrink-0" />
        <PhaseStep label="1 · Title/Abstract" active={false} />
        <ArrowRight className="h-3 w-3 shrink-0" />
        <PhaseStep label="2 · Full Text" active={false} />
        <span className="ml-auto flex items-center gap-1">
          <Info className="h-3 w-3" />
          Assign to a review to add to its screening queue
        </span>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading unallocated studies…
        </div>
      )}

      {!isLoading && studies.length === 0 && (
        <div className="text-center py-16 border rounded-lg bg-card text-muted-foreground">
          <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
          <p className="font-semibold text-base text-foreground">All studies are allocated</p>
          <p className="text-sm mt-1">Every imported paper has been assigned to at least one review.</p>
        </div>
      )}

      {/* ── List view ── */}
      {viewMode === "list" && studies.length > 0 && (
        <div className="space-y-3">
          {studies.map((study) => (
            <UnallocatedListRow
              key={study.id}
              study={study}
              reviews={reviews}
              assigning={assigning}
              confirming={confirming}
              onAssign={(studyId, reviewId) => {
                setAssigning(studyId);
                assignMutation.mutate({ studyId, reviewId });
              }}
              onConfirmExclude={(studyId) => {
                setConfirming(studyId);
                confirmExcludedMutation.mutate(studyId);
              }}
              onOpen={() => {
                setIndex(studies.indexOf(study));
                setViewMode("card");
              }}
            />
          ))}
        </div>
      )}

      {/* ── Card view ── */}
      {viewMode === "card" && current && (
        <div className="border rounded-lg bg-card">
          <div className="flex items-center justify-between px-5 py-3 border-b">
            <button onClick={() => nav(-1)} disabled={index === 0}
              className="p-1 rounded hover:bg-accent disabled:opacity-30">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-muted-foreground">
              {index + 1} / {studies.length} unallocated
            </span>
            <button onClick={() => nav(1)} disabled={index === studies.length - 1}
              className="p-1 rounded hover:bg-accent disabled:opacity-30">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="p-5 space-y-5">
            <div>
              <h2 className="text-lg font-semibold mb-1">{current.title}</h2>
              <p className="text-sm text-muted-foreground">
                {current.authors.slice(0, 3).join(", ")}
                {current.authors.length > 3 ? " et al." : ""}
                {current.year ? ` (${current.year})` : ""}
                {current.journal ? `, ${current.journal}` : ""}
              </p>
              {current.doi && (
                <a
                  href={`https://doi.org/${current.doi.replace(/^https?:\/\/doi\.org\//i, "")}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-primary hover:underline mt-2 w-fit"
                >
                  <ExternalLink className="h-3 w-3" /> DOI: {current.doi}
                </a>
              )}
            </div>

            {current.abstract ? (
              <p className="text-sm leading-relaxed border-l-2 border-border pl-4">{current.abstract}</p>
            ) : (
              <p className="text-sm text-muted-foreground italic">No abstract available.</p>
            )}

            {current.screeningDecisions.length > 0 ? (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Bot className="h-3.5 w-3.5" /> AI Predictions
                </p>
                <div className="divide-y border rounded-md overflow-hidden">
                  {current.screeningDecisions.map((d) => {
                    let reasoning = "";
                    let keyFactors: string[] = [];
                    try {
                      const p = JSON.parse(d.aiRawResponse ?? "{}") as { reasoning?: string; keyFactors?: string[] };
                      reasoning = p.reasoning ?? "";
                      keyFactors = p.keyFactors ?? [];
                    } catch { /* ignore */ }
                    return (
                      <div key={d.review.id} className="p-3 text-sm">
                        <div className="flex items-center gap-3 flex-wrap mb-1.5">
                          <span className="font-medium text-xs">{d.review.name}</span>
                          <DecisionBadge decision={d.decision as "INCLUDE" | "EXCLUDE" | "UNCERTAIN"} />
                          {d.aiConfidenceScore != null && (
                            <span className="text-xs text-muted-foreground">
                              {Math.round(d.aiConfidenceScore * 100)}% confidence
                            </span>
                          )}
                        </div>
                        {reasoning && <p className="text-xs text-muted-foreground leading-relaxed">{reasoning}</p>}
                        {keyFactors.length > 0 && (
                          <ul className="text-xs text-muted-foreground list-disc list-inside mt-1 space-y-0.5">
                            {keyFactors.map((f, i) => <li key={i}>{f}</li>)}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-md bg-secondary/50 text-sm text-muted-foreground flex items-center gap-2">
                <Bot className="h-4 w-4 shrink-0" />
                No AI predictions available yet. Batch screening may still be running.
              </div>
            )}

            <div className="border-t pt-4 space-y-3">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <UserCheck className="h-3.5 w-3.5" /> Override: assign to a review
                </p>
                <div className="flex flex-wrap gap-2">
                  {reviews.map((r) => {
                    const aiForReview = current.screeningDecisions.find((d) => d.review.id === r.id);
                    const isExclude = aiForReview?.decision === "EXCLUDE";
                    const isAssigning = assigning === current.id && assignMutation.isPending;
                    return (
                      <button
                        key={r.id}
                        onClick={() => { setAssigning(current.id); assignMutation.mutate({ studyId: current.id, reviewId: r.id }); }}
                        disabled={assignMutation.isPending || confirmExcludedMutation.isPending}
                        title={isExclude ? `AI suggested EXCLUDE (${Math.round((aiForReview.aiConfidenceScore ?? 0) * 100)}% confidence) - click to override` : undefined}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border font-medium transition-colors disabled:opacity-50 ${isExclude ? "text-muted-foreground hover:border-primary/50 hover:text-foreground" : "border-primary/50 text-primary hover:bg-primary/5"}`}
                      >
                        {isAssigning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />}
                        {r.name}
                        {isExclude && <span className="text-xs opacity-60 ml-0.5">(AI: exclude)</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center gap-3 pt-1 border-t">
                <button
                  onClick={() => { setConfirming(current.id); confirmExcludedMutation.mutate(current.id); }}
                  disabled={assignMutation.isPending || confirmExcludedMutation.isPending}
                  title="Mark as excluded (creates a human-verified exclusion record based on AI reasoning)"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-destructive/40 text-destructive hover:bg-destructive/5 font-medium transition-colors disabled:opacity-50"
                >
                  {confirming === current.id && confirmExcludedMutation.isPending
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Ban className="h-3.5 w-3.5" />}
                  Confirm excluded
                </button>
                <span className="text-xs text-muted-foreground">
                  Agree with AI: records human-verified exclusion without assigning to a review
                </span>
                <button
                  onClick={() => nav(1)}
                  disabled={index === studies.length - 1}
                  className="px-3 py-1.5 rounded-md text-sm border text-muted-foreground hover:bg-accent disabled:opacity-30 ml-auto"
                >
                  Skip →
                </button>
              </div>

              {(assignMutation.isError || confirmExcludedMutation.isError) && (
                <p className="text-xs text-destructive">
                  {((assignMutation.error ?? confirmExcludedMutation.error) as Error).message}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Unallocated list row ─────────────────────────────────────────────────────

function UnallocatedListRow({
  study,
  reviews,
  assigning,
  confirming,
  onAssign,
  onConfirmExclude,
  onOpen,
}: {
  study: UnallocatedStudy;
  reviews: ReviewOption[];
  assigning: string | null;
  confirming: string | null;
  onAssign: (studyId: string, reviewId: string) => void;
  onConfirmExclude: (studyId: string) => void;
  onOpen: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      {/* Summary row */}
      <div className="flex items-start gap-3 p-4">
        <div className="flex-1 min-w-0">
          <button
            onClick={onOpen}
            className="text-sm font-semibold text-left hover:text-primary transition-colors line-clamp-2"
          >
            {study.title}
          </button>
          <p className="text-xs text-muted-foreground mt-0.5">
            {study.authors.slice(0, 2).join(", ")}
            {study.authors.length > 2 ? " et al." : ""}
            {study.year ? ` · ${study.year}` : ""}
            {study.journal ? ` · ${study.journal}` : ""}
          </p>
        </div>

        {/* AI decisions compact */}
        <div className="flex flex-wrap gap-1.5 shrink-0">
          {study.screeningDecisions.map((d) => (
            <span
              key={d.review.id}
              title={`${d.review.name}: ${d.decision} (${Math.round((d.aiConfidenceScore ?? 0) * 100)}% confidence)`}
              className="flex items-center gap-1 text-xs"
            >
              <DecisionBadge decision={d.decision as "INCLUDE" | "EXCLUDE" | "UNCERTAIN"} />
              <span className="text-muted-foreground/70">{Math.round((d.aiConfidenceScore ?? 0) * 100)}%</span>
            </span>
          ))}
          {study.screeningDecisions.length === 0 && (
            <span className="text-xs text-muted-foreground italic flex items-center gap-1">
              <Bot className="h-3 w-3" /> No AI yet
            </span>
          )}
        </div>

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
        >
          {expanded ? "▲ Less" : "▼ More"}
        </button>
      </div>

      {/* Expanded: abstract + reasoning + assign buttons */}
      {expanded && (
        <div className="border-t px-4 pb-4 pt-3 space-y-3">
          {study.abstract && (
            <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-border pl-3">
              {study.abstract}
            </p>
          )}

          {study.screeningDecisions.length > 0 && (
            <div className="divide-y border rounded-md overflow-hidden text-xs">
              {study.screeningDecisions.map((d) => {
                let reasoning = "";
                try {
                  reasoning = (JSON.parse(d.aiRawResponse ?? "{}") as { reasoning?: string }).reasoning ?? "";
                } catch { /* ignore */ }
                return (
                  <div key={d.review.id} className="p-2.5 flex gap-3">
                    <div className="shrink-0 w-32 font-medium text-muted-foreground">{d.review.name}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <DecisionBadge decision={d.decision as "INCLUDE" | "EXCLUDE" | "UNCERTAIN"} />
                        {d.aiConfidenceScore != null && (
                          <span className="text-muted-foreground">{Math.round(d.aiConfidenceScore * 100)}% confidence</span>
                        )}
                      </div>
                      {reasoning && <p className="text-muted-foreground leading-relaxed">{reasoning}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="space-y-2 pt-1">
            <div className="flex flex-wrap gap-2">
              {reviews.map((r) => {
                const aiForReview = study.screeningDecisions.find((d) => d.review.id === r.id);
                const isExclude = aiForReview?.decision === "EXCLUDE";
                const isAssigning = assigning === study.id;
                return (
                  <button
                    key={r.id}
                    onClick={() => onAssign(study.id, r.id)}
                    disabled={isAssigning || confirming === study.id}
                    title={isExclude ? `AI suggested EXCLUDE (${Math.round((aiForReview.aiConfidenceScore ?? 0) * 100)}%) - click to override` : undefined}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border font-medium transition-colors disabled:opacity-50 ${isExclude ? "text-muted-foreground hover:border-primary/50 hover:text-foreground" : "border-primary/50 text-primary hover:bg-primary/5"}`}
                  >
                    {isAssigning ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserCheck className="h-3 w-3" />}
                    {r.name}
                    {isExclude && <span className="opacity-60"> (AI: exclude)</span>}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onConfirmExclude(study.id)}
                disabled={assigning === study.id || confirming === study.id}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border border-destructive/40 text-destructive hover:bg-destructive/5 font-medium transition-colors disabled:opacity-50"
                title="Confirm AI exclusion (creates a human-verified exclusion record)"
              >
                {confirming === study.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
                Confirm excluded
              </button>
              <button
                onClick={onOpen}
                className="px-2.5 py-1 rounded-md text-xs border text-muted-foreground hover:bg-accent ml-auto"
              >
                Full view →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Normal screening queue ───────────────────────────────────────────────────

function ScreeningQueue({
  reviewId,
  reviews,
  onReviewChange,
}: {
  reviewId: string;
  reviews: ReviewOption[];
  onReviewChange: (id: string) => void;
}) {
  const [phase, setPhase] = useState<"TITLE_ABSTRACT" | "FULL_TEXT" | "EXTRACTION" | "COMPLETED">("TITLE_ABSTRACT");
  // Extraction and Completed are lists of finished screening, not screening phases
  const isListStage = phase === "EXTRACTION" || phase === "COMPLETED";
  // List ("Show all") is the default: after a decision the card silently advancing
  // to the next paper looked like the click did nothing. Reviewers open a card
  // from the list, decide, and land back on the list with a confirmation banner.
  const [viewMode, setViewMode] = useState<"card" | "list">("list");
  const [studyIndex, setStudyIndex] = useState(0);
  const [reason, setReason] = useState("");
  const [pendingDecision, setPendingDecision] = useState<"INCLUDE" | "EXCLUDE" | "UNCERTAIN" | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  // Reviewer's own marks in the AI criteria table, keyed by criterion id (I1, E2 ...)
  const [criteriaMarks, setCriteriaMarks] = useState<Record<string, Verdict>>({});
  const currentReview = reviews.find((r) => r.id === reviewId);
  const checklistCodes =
    pendingDecision === "INCLUDE" ? currentReview?.inclusionCategories ?? []
    : pendingDecision === "EXCLUDE" ? currentReview?.exclusionCategories ?? []
    : [];
  const [aiPrediction, setAiPrediction] = useState<AiPrediction | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  // Shown inline under the decision buttons. Previously an auto-run failure was
  // swallowed with nothing on screen, which looked like the AI never ran.
  const [aiError, setAiError] = useState<string | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [lastDecision, setLastDecision] = useState<{ title: string; decision: string } | null>(null);

  const qc = useQueryClient();
  const { user } = useAuthStore();

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  useEffect(() => { setPage(1); }, [reviewId, phase]);

  const { data, isLoading } = useQuery({
    queryKey: ["screening-queue", reviewId, phase, page],
    queryFn: () =>
      api.get<{ studies: StudyItem[]; total: number; totalPages: number }>(
        `/screening/queue?reviewId=${reviewId}&phase=${phase}&page=${page}&pageSize=${PAGE_SIZE}`
      ),
    enabled: !!reviewId && !isListStage,
  });

  const studies = data?.studies ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const current = studies[studyIndex];
  // A decision can empty the last page; step back rather than show "queue complete".
  useEffect(() => {
    if (data && page > totalPages) { setPage(totalPages); setStudyIndex(0); }
  }, [data, page, totalPages]);

  const goToPage = (p: number) => { setPage(p); setStudyIndex(0); setAiPrediction(null); setPendingDecision(null); };
  // Card arrows step across page boundaries, so the card view is never stuck at 20 papers.
  const stepCard = (delta: 1 | -1) => {
    setAiPrediction(null); setPendingDecision(null);
    if (delta === 1 && studyIndex < studies.length - 1) return setStudyIndex((i) => i + 1);
    if (delta === -1 && studyIndex > 0) return setStudyIndex((i) => i - 1);
    if (delta === 1 && page < totalPages) { setPage(page + 1); setStudyIndex(0); }
    if (delta === -1 && page > 1) { setPage(page - 1); setStudyIndex(PAGE_SIZE - 1); }
  };
  const globalIndex = (page - 1) * PAGE_SIZE + studyIndex + 1;

  const decisionMutation = useMutation({
    mutationFn: (body: {
      decision: string;
      exclusionReason?: string;
      exclusionReasonCodes?: string[];
      inclusionReason?: string;
      inclusionReasonCodes?: string[];
      uncertainReason?: string;
      criteriaChecks?: Array<{ id: string; kind: string; criterion: string; ai: Verdict | null; reviewer: Verdict }>;
    }) =>
      api.post("/screening/decision", { studyId: current?.id, reviewId, phase, ...body }),
    onSuccess: (_result, variables) => {
      void qc.invalidateQueries({ queryKey: ["screening-queue"] });
      void qc.invalidateQueries({ queryKey: ["review-stats"] });
      if (current) setLastDecision({ title: current.title, decision: variables.decision });
      setStudyIndex(0);
      setReason("");
      setSelectedCodes(new Set());
      setCriteriaMarks({});
      setPendingDecision(null);
      setAiPrediction(null);
      // Return to the list so the recorded decision is visible instead of
      // silently swapping in the next paper.
      setViewMode("list");
    },
  });

  const criteriaRows = (): CriterionRow[] => {
    const stored = current?.screeningDecisions.find((x) => x.isAiSuggested);
    const aiRows = aiPrediction?.suggestion.criteria?.length
      ? aiPrediction.suggestion.criteria
      : stored ? storedAiView(stored).criteria : [];
    return mergeCriteria(currentReview?.criteria ?? [], aiRows);
  };
  // At full text the decision must be documented: every criterion marked and a reason given.
  const fullTextProblems = (): string[] => {
    if (phase !== "FULL_TEXT") return [];
    const problems: string[] = [];
    if (!reason.trim()) problems.push("give a short reason for your decision");
    const unmarked = criteriaRows().filter((r) => !criteriaMarks[r.id]).length;
    if (unmarked > 0) problems.push(`mark every criterion as met, not met or unclear (${unmarked} still unmarked)`);
    return problems;
  };
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Turns the reviewer's table marks into a readable exclusion reason, so
  // marking the criteria is enough and nothing has to be typed twice.
  const reasonFromMarks = (): string => {
    const rows = criteriaRows();
    const notMet = rows.filter((r) => r.kind === "INCLUSION" && criteriaMarks[r.id] === "NO").map((r) => r.criterion);
    const applies = rows.filter((r) => r.kind === "EXCLUSION" && criteriaMarks[r.id] === "YES").map((r) => r.criterion);
    return [
      notMet.length ? `Not met: ${notMet.join("; ")}` : "",
      applies.length ? `Exclusion applies: ${applies.join("; ")}` : "",
    ].filter(Boolean).join(". ");
  };

  const handleDecisionClick = (d: "INCLUDE" | "EXCLUDE" | "UNCERTAIN") => {
    setConfirmError(null);
    setPendingDecision(d);
    setReason(d === "EXCLUDE" ? reasonFromMarks() : "");
    setSelectedCodes(new Set());
  };

  const toggleCode = (code: string) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const handleConfirm = () => {
    if (!pendingDecision) return;
    setConfirmError(null);
    const problems = fullTextProblems();
    if (problems.length > 0) {
      setConfirmError(`Before submitting: ${problems.join("; ")}.`);
      return;
    }
    if (pendingDecision === "EXCLUDE" && !reason.trim()) {
      setConfirmError("Please give a reason for excluding this paper.");
      return;
    }
    const rows = criteriaRows();
    const criteriaChecks = rows
      .filter((r) => criteriaMarks[r.id])
      .map((r) => ({ id: r.id, kind: r.kind, criterion: r.criterion, ai: r.verdict, reviewer: criteriaMarks[r.id]! }));
    // When the table rows are the review's own checklist items, the marks
    // double as the structured reason codes the checklist used to collect.
    const codeSet = new Set(selectedCodes);
    for (const r of rows) {
      if (pendingDecision === "INCLUDE" && r.kind === "INCLUSION" && criteriaMarks[r.id] === "YES"
        && currentReview?.inclusionCategories?.includes(r.criterion)) codeSet.add(r.criterion);
      if (pendingDecision === "EXCLUDE" && r.kind === "EXCLUSION" && criteriaMarks[r.id] === "YES"
        && currentReview?.exclusionCategories?.includes(r.criterion)) codeSet.add(r.criterion);
    }
    const codes = Array.from(codeSet);
    decisionMutation.mutate({
      criteriaChecks: criteriaChecks.length > 0 ? criteriaChecks : undefined,
      decision: pendingDecision,
      exclusionReason: pendingDecision === "EXCLUDE" ? reason.trim() || undefined : undefined,
      exclusionReasonCodes: pendingDecision === "EXCLUDE" && codes.length > 0 ? codes : undefined,
      inclusionReason: pendingDecision === "INCLUDE" ? reason.trim() || undefined : undefined,
      inclusionReasonCodes: pendingDecision === "INCLUDE" && codes.length > 0 ? codes : undefined,
      uncertainReason: pendingDecision === "UNCERTAIN" ? reason.trim() || undefined : undefined,
    });
  };

  const handleAiPredict = async (silent = false) => {
    if (!current) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await api.post<AiPrediction>("/screening/ai-predict", {
        studyId: current.id, reviewId, phase,
        // An explicit "Re-run AI" click must actually re-run, not return the cached answer
        ...(silent ? {} : { force: true }),
      });
      setAiPrediction(result);
    } catch (err) {
      // Never a popup, auto-triggered or not: the reason is shown inline
      // under the buttons and the reviewer can still screen without AI.
      // `silent` is kept so call sites read clearly, but both paths now
      // surface the server's actual message.
      void silent;
      setAiError(err instanceof Error ? err.message : "AI suggestion failed.");
    } finally {
      setAiLoading(false);
    }
  };

  // Runs AI screening the moment a reviewer opens a study that doesn't have a
  // suggestion yet, instead of pre-screening a whole batch on import (which
  // spends money on records nobody may ever review) or leaving reviewers to
  // screen cold with no AI help at all. Mirrors the existing FULL_TEXT
  // auto-trigger on PDF upload; this covers TITLE_ABSTRACT, the phase every
  // newly imported study starts in.
  useEffect(() => {
    if (viewMode !== "card" || !current || phase !== "TITLE_ABSTRACT") return;
    // A suggestion saved before the criteria table existed counts as missing,
    // so opening the paper refreshes it once into the new format.
    const hasAiSuggestion = current.screeningDecisions.some((d) => d.isAiSuggested && (d.aiRawResponse ?? "").includes('"criteria"'));
    if (hasAiSuggestion || aiPrediction || aiLoading) return;
    void handleAiPredict(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, viewMode, phase]);

  useEffect(() => { setAiError(null); setCriteriaMarks({}); }, [current?.id]);

  const handleUploadFullText = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !current) return;
    setUploadLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await authFetch(`/api/extraction/${current.id}/upload-pdf`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? "PDF upload failed.");
      }
      void qc.invalidateQueries({ queryKey: ["screening-queue"] });
      // Auto-run AI immediately after upload so reviewers see the full-text analysis without pressing a button
      void handleAiPredict(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : "PDF upload failed.");
    } finally {
      setUploadLoading(false);
      e.target.value = "";
    }
  };

  const switchPhase = (p: "TITLE_ABSTRACT" | "FULL_TEXT" | "EXTRACTION" | "COMPLETED") => {
    setPhase(p);
    setStudyIndex(0);
    setAiPrediction(null);
    setPendingDecision(null);
    setLastDecision(null);
    setViewMode("list");
  };

  const aiSuggestion = current?.screeningDecisions.find((d) => d.isAiSuggested);
  const aiView: AiView | null = aiPrediction
    ? {
        decision: aiPrediction.suggestion.decision,
        confidence: aiPrediction.suggestion.confidence,
        highlights: aiPrediction.suggestion.highlights ?? aiPrediction.suggestion.keyFactors,
        criteria: aiPrediction.suggestion.criteria ?? [],
        summary: aiPrediction.suggestion.summary ?? aiPrediction.suggestion.reasoning,
      }
    : aiSuggestion ? storedAiView(aiSuggestion) : null;
  const isCoordinatorOrAdmin = user?.role === "COORDINATOR" || user?.role === "ADMIN";
  const otherDecisions = current?.screeningDecisions.filter(
    (d) => !d.isAiSuggested && d.reviewerId !== user?.id
  ) ?? [];

  const doiUrl = current?.doi
    ? `https://doi.org/${current.doi.replace(/^https?:\/\/doi\.org\//i, "")}`
    : null;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-2xl font-bold">Screening</h1>
        {studies.length > 0 && !isListStage && (
          <span className="ml-auto flex items-center gap-1 border rounded-md overflow-hidden text-xs">
            <button
              onClick={() => setViewMode("card")}
              title="Card view: one study at a time"
              className={`flex items-center gap-1.5 px-2.5 py-1.5 ${viewMode === "card" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Card
            </button>
            <button
              onClick={() => setViewMode("list")}
              title="List view: show all in queue"
              className={`flex items-center gap-1.5 px-2.5 py-1.5 border-l ${viewMode === "list" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
            >
              <LayoutList className="h-3.5 w-3.5" /> Show all
            </button>
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-5 p-3 bg-secondary/50 rounded-lg">
        <PhaseStep label="1 · Title/Abstract" active={phase === "TITLE_ABSTRACT"} />
        <ArrowRight className="h-3 w-3 shrink-0" />
        <PhaseStep label="2 · Full Text" active={phase === "FULL_TEXT"} />
        <ArrowRight className="h-3 w-3 shrink-0" />
        <PhaseStep label="3 · Data Extraction" active={phase === "EXTRACTION"} />
        <ArrowRight className="h-3 w-3 shrink-0" />
        <PhaseStep label="4 · Completed" active={phase === "COMPLETED"} />
        <span className="ml-auto flex items-center gap-1 text-muted-foreground">
          <Info className="h-3 w-3" />
          {phase === "TITLE_ABSTRACT"
            ? "INCLUDE moves studies to Full Text queue"
            : phase === "FULL_TEXT"
              ? "INCLUDE moves studies to Extraction"
              : phase === "EXTRACTION"
                ? "Confirming both extraction and quality appraisal moves a paper to Completed"
                : "Finished papers: open one to review or change it"}
        </span>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Review</label>
          <select
            value={reviewId}
            onChange={(e) => {
              onReviewChange(e.target.value);
              setStudyIndex(0);
              setAiPrediction(null);
              setPendingDecision(null);
            }}
            className="border rounded-md px-3 py-1.5 text-sm bg-background"
          >
            {reviews.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Phase</label>
          <div className="flex rounded-md border overflow-hidden text-sm">
            <button
              onClick={() => switchPhase("TITLE_ABSTRACT")}
              className={`px-3 py-1.5 ${phase === "TITLE_ABSTRACT" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
            >
              Title/Abstract
            </button>
            <button
              onClick={() => switchPhase("FULL_TEXT")}
              className={`px-3 py-1.5 border-l ${phase === "FULL_TEXT" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
            >
              Full Text
            </button>
            <button
              onClick={() => switchPhase("EXTRACTION")}
              className={`px-3 py-1.5 border-l ${phase === "EXTRACTION" ? "bg-brand-magenta text-white" : "hover:bg-accent"}`}
            >
              Extraction
            </button>
            <button
              onClick={() => switchPhase("COMPLETED")}
              className={`px-3 py-1.5 border-l ${phase === "COMPLETED" ? "bg-green-600 text-white" : "hover:bg-accent"}`}
              title="Papers whose extraction and quality appraisal are both confirmed"
            >
              Completed
            </button>
          </div>
        </div>
      </div>

      {/* Decision confirmation banner */}
      {lastDecision && !isListStage && (
        <div className="mb-4 flex items-start justify-between gap-3 p-3 border border-green-200 bg-green-50 rounded-md text-sm text-green-800">
          <p className="flex items-start gap-2 min-w-0">
            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              <strong>{lastDecision.decision}</strong> recorded for{" "}
              <span className="italic">"{lastDecision.title.length > 90 ? lastDecision.title.slice(0, 90) + "…" : lastDecision.title}"</span>
            </span>
          </p>
          <button onClick={() => setLastDecision(null)} className="text-green-700/60 hover:text-green-800 flex-shrink-0">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      {phase === "EXTRACTION" && reviewId && (
        <ExtractionQueuePanel reviewId={reviewId} stage="extraction" />
      )}
      {phase === "COMPLETED" && reviewId && (
        <ExtractionQueuePanel reviewId={reviewId} stage="completed" />
      )}

      {isLoading && !isListStage && (
        <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading queue…
        </div>
      )}

      {!isLoading && !isListStage && studies.length === 0 && reviewId && (
        <div className="text-center py-16 text-muted-foreground border rounded-lg bg-card">
          <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
          <p className="font-semibold text-base text-foreground">Queue complete for this phase</p>
          {phase === "TITLE_ABSTRACT" ? (
            <p className="text-sm mt-2 max-w-sm mx-auto">
              All title/abstract decisions recorded. Studies you included are waiting in the{" "}
              <button onClick={() => switchPhase("FULL_TEXT")} className="text-primary underline underline-offset-2">
                Full Text queue →
              </button>
            </p>
          ) : (
            <p className="text-sm mt-2 max-w-sm mx-auto">
              All full-text decisions recorded. Studies you included are now <strong>INCLUDED</strong> and
              waiting in the{" "}
              <button onClick={() => switchPhase("EXTRACTION")} className="text-brand-magenta underline underline-offset-2">
                Extraction queue →
              </button>
            </p>
          )}
        </div>
      )}

      {!reviewId && !isLoading && (
        <div className="text-center py-16 text-muted-foreground border rounded-lg bg-card">
          <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-muted-foreground/50" />
          <p className="text-sm">Loading reviews…</p>
        </div>
      )}

      {/* ── List view ── */}
      {viewMode === "list" && studies.length > 0 && (
        <div className="space-y-2">
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={goToPage} />
          {studies.map((study, i) => {
            const aiDecision = study.screeningDecisions.find((d) => d.isAiSuggested);
            let confidence: number | null = null;
            if (aiDecision?.aiConfidenceScore != null) confidence = aiDecision.aiConfidenceScore;
            return (
              <button
                key={study.id}
                onClick={() => { setStudyIndex(i); setViewMode("card"); setAiPrediction(null); setPendingDecision(null); }}
                className={`w-full text-left border rounded-lg p-3 bg-card hover:border-primary/50 hover:shadow-sm transition-all ${i === studyIndex ? "border-primary/60 bg-primary/5" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-1">{study.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {study.authors.slice(0, 2).join(", ")}
                      {study.authors.length > 2 ? " et al." : ""}
                      {study.year ? ` · ${study.year}` : ""}
                      {study.journal ? ` · ${study.journal}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {aiDecision && (
                      <>
                        <DecisionBadge decision={aiDecision.decision as "INCLUDE" | "EXCLUDE" | "UNCERTAIN"} />
                        {confidence != null && (
                          <span className="text-xs text-muted-foreground">{Math.round(confidence * 100)}%</span>
                        )}
                      </>
                    )}
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Card view ── */}
      {viewMode === "card" && current && (
        <div className="border rounded-lg bg-card">
          <div className="flex items-center justify-between px-5 py-3 border-b">
            <button
              onClick={() => stepCard(-1)}
              disabled={globalIndex <= 1}
              className="p-1 rounded hover:bg-accent disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-muted-foreground">
              {globalIndex} of {total} pending
            </span>
            <button
              onClick={() => stepCard(1)}
              disabled={globalIndex >= total}
              className="p-1 rounded hover:bg-accent disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <h2 className="text-lg font-semibold mb-1">{current.title}</h2>
              <p className="text-sm text-muted-foreground">
                {current.authors.slice(0, 3).join(", ")}
                {current.authors.length > 3 ? " et al." : ""}
                {current.year ? ` (${current.year})` : ""}
                {current.journal ? `, ${current.journal}` : ""}
              </p>
              <div className="flex flex-wrap gap-3 mt-2">
                {doiUrl && (
                  <a href={doiUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-primary hover:underline">
                    <ExternalLink className="h-3 w-3" /> DOI: {current.doi}
                  </a>
                )}
                {current.fullTextUrl && (
                  <a href={current.fullTextUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-primary hover:underline">
                    <ExternalLink className="h-3 w-3" /> Full text
                  </a>
                )}
                {current.fullTextPath && (
                  <button
                    onClick={async () => {
                      const res = await authFetch(`/api/studies/${current.id}/pdf`);
                      if (!res.ok) return;
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      window.open(url, "_blank");
                      setTimeout(() => URL.revokeObjectURL(url), 30000);
                    }}
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <FileText className="h-3 w-3" /> View uploaded PDF
                  </button>
                )}
              </div>
            </div>

            {current.abstract ? (
              <p className="text-sm leading-relaxed border-l-2 border-border pl-4">{current.abstract}</p>
            ) : (
              <p className="text-sm text-muted-foreground italic">No abstract available.</p>
            )}

            {phase === "FULL_TEXT" && (
              <div className="p-3 rounded-md border border-dashed bg-secondary/30 text-sm">
                <p className="font-medium text-xs text-muted-foreground uppercase tracking-wide mb-2">Full Text Access</p>
                {!current.fullTextPath && !current.fullTextUrl && (
                  <p className="text-xs text-amber-700 mb-2">
                    No full text available yet. Access via DOI above, then upload the PDF so the AI suggestion and extraction pre-fill can use it.
                  </p>
                )}
                {current.fullTextPath && (
                  <p className="text-xs text-green-700 mb-2">
                    PDF uploaded. Use "View uploaded PDF" above to read it, or replace it below.
                  </p>
                )}
                <label className="flex items-center gap-2 cursor-pointer text-xs text-primary hover:underline w-fit">
                  {uploadLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                  {current.fullTextPath ? "Replace PDF" : "Upload PDF for extraction"}
                  <input type="file" accept=".pdf" className="hidden" onChange={handleUploadFullText} disabled={uploadLoading} />
                </label>
              </div>
            )}

            {otherDecisions.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Co-reviewer:</span>
                {otherDecisions.map((d, i) => (
                  <span key={i} className="flex items-center gap-1">
                    <DecisionBadge decision={d.decision as "INCLUDE" | "EXCLUDE" | "UNCERTAIN"} />
                    {d.exclusionReason && <span className="italic">({d.exclusionReason})</span>}
                    {d.inclusionReason && <span className="italic">({d.inclusionReason})</span>}
                    {d.uncertainReason && <span className="italic">({d.uncertainReason})</span>}
                  </span>
                ))}
              </div>
            )}

            {/* In FULL_TEXT phase: show prior abstract-based suggestion as read-only context */}
            {phase === "FULL_TEXT" && current?.titleAbstractAiDecision && (
              <div className="p-3 rounded-md border border-blue-200 bg-blue-50 space-y-1">
                <div className="flex items-center gap-2">
                  <Bot className="h-3.5 w-3.5 text-blue-600" />
                  <span className="text-xs font-medium text-blue-700">Abstract screening (context only)</span>
                  <PromptPeek studyId={current.id} reviewId={reviewId} phase="TITLE_ABSTRACT" />
                </div>
                <AiSuggestionBody view={storedAiView(current.titleAbstractAiDecision)} compact />
              </div>
            )}

            {aiView && (
              <div className="p-4 rounded-md border bg-secondary/50 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Bot className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">
                    {phase === "FULL_TEXT" && current?.titleAbstractAiDecision ? "Full-text supplement" : "AI suggestion"}
                  </span>
                  {current && !isListStage && (
                    <PromptPeek
                      studyId={current.id}
                      reviewId={reviewId}
                      phase={phase}
                      // Refetch after a fresh AI run so the panel never shows the previous prompt
                      version={aiPrediction ? `${aiPrediction.cached}-${aiPrediction.suggestion.summary ?? aiPrediction.suggestion.reasoning}` : "stored"}
                    />
                  )}
                  <TextSourceBadge
                    textSource={
                      aiPrediction?.textSource ??
                      (() => {
                        try {
                          return (JSON.parse(aiSuggestion?.aiRawResponse ?? "{}") as { textSource?: string }).textSource as "full_text" | "abstract" | "title_only" | "scanned_pdf" | undefined;
                        } catch { return undefined; }
                      })()
                    }
                    phase={phase === "FULL_TEXT" ? "FULL_TEXT" : "TITLE_ABSTRACT"}
                  />
                  {aiPrediction?.cached && <span className="text-xs text-muted-foreground italic">cached</span>}
                  <span className="text-xs text-amber-600 font-medium ml-auto">Requires human confirmation</span>
                </div>
                {aiPrediction?.suggestion.charsAvailable != null && aiPrediction.textSource === "full_text" && (
                  <CharCoverageBadge
                    charsAvailable={aiPrediction.suggestion.charsAvailable}
                    charsUsed={aiPrediction.suggestion.charsUsed ?? aiPrediction.suggestion.charsAvailable}
                    aiMaxChars={aiPrediction.aiMaxChars ?? 60000}
                  />
                )}
                {aiView && (
                  <AiSuggestionBody
                    view={aiView}
                    criteria={criteriaRows()}
                    requireAll={phase === "FULL_TEXT"}
                    marks={criteriaMarks}
                    onMark={(id, value) => setCriteriaMarks((prev) => {
                      const next = { ...prev };
                      if (value === null) delete next[id]; else next[id] = value;
                      return next;
                    })}
                  />
                )}
              </div>
            )}

            {/* No AI suggestion (still running, failed, or not run): the reviewer
                still gets the review's criteria to mark, which full text requires. */}
            {!aiView && !aiLoading && criteriaRows().length > 0 && (
              <div className="p-4 rounded-md border bg-secondary/30 space-y-2">
                <p className="text-sm font-medium">Criteria check</p>
                <CriteriaTable
                  criteria={criteriaRows()}
                  requireAll={phase === "FULL_TEXT"}
                  marks={criteriaMarks}
                  onMark={(id, value) => setCriteriaMarks((prev) => {
                    const next = { ...prev };
                    if (value === null) delete next[id]; else next[id] = value;
                    return next;
                  })}
                />
              </div>
            )}

            {pendingDecision && (
              <div className="p-4 rounded-md border border-primary/30 bg-primary/5 space-y-3">
                <p className="text-sm font-medium flex items-center gap-2">
                  Confirm decision: <DecisionBadge decision={pendingDecision} />
                </p>
                <div>
                  <label className="block text-xs font-medium mb-1">
                    {phase === "FULL_TEXT"
                      ? "Reason for your decision (required at full text)"
                      : pendingDecision === "EXCLUDE" ? "Exclusion reason (required)" : "Rationale (optional)"}
                  </label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
                    placeholder={
                      pendingDecision === "EXCLUDE"
                        ? "e.g. Wrong population, or no relevant outcome reported"
                        : "e.g. Meets all criteria on the abstract"
                    }
                    className="w-full px-3 py-2 border rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                {checklistCodes.length > 0 && criteriaRows().length === 0 && (
                  <div>
                    <label className="block text-xs font-medium mb-1">
                      Which criteria apply? (optional, select any that fit)
                    </label>
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                      {checklistCodes.map((code) => (
                        <label key={code} className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedCodes.has(code)}
                            onChange={() => toggleCode(code)}
                          />
                          {code}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={handleConfirm}
                    disabled={decisionMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                  >
                    {decisionMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Confirm
                  </button>
                  <button
                    onClick={() => { setPendingDecision(null); setReason(""); setSelectedCodes(new Set()); }}
                    className="px-4 py-2 rounded-md border text-sm font-medium hover:bg-accent"
                  >
                    Cancel
                  </button>
                </div>
                {confirmError && <p className="text-xs text-destructive">{confirmError}</p>}
                {decisionMutation.isError && (
                  <p className="text-xs text-destructive">{(decisionMutation.error as Error).message}</p>
                )}
              </div>
            )}

            {!pendingDecision && (
              <div className="flex flex-wrap gap-3 pt-1">
                <button onClick={() => handleDecisionClick("INCLUDE")} disabled={decisionMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-green-500 text-white text-sm font-medium hover:bg-green-600 disabled:opacity-50">
                  <CheckCircle2 className="h-4 w-4" /> Include
                </button>
                <button onClick={() => handleDecisionClick("EXCLUDE")} disabled={decisionMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 rounded-md bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 disabled:opacity-50">
                  <XCircle className="h-4 w-4" /> Exclude
                </button>
                <button onClick={() => handleDecisionClick("UNCERTAIN")} disabled={decisionMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-medium hover:bg-accent disabled:opacity-50">
                  <HelpCircle className="h-4 w-4" /> Uncertain
                </button>
                {/* Show AI loading indicator for all roles when auto-trigger is running */}
                {aiLoading && !isCoordinatorOrAdmin && (
                  <span className="flex items-center gap-1.5 ml-auto text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {phase === "FULL_TEXT" ? "Analysing full text…" : "Analysing abstract…"}
                  </span>
                )}
                {isCoordinatorOrAdmin && (
                  <button onClick={() => handleAiPredict()} disabled={aiLoading || decisionMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 rounded-md border border-primary text-primary text-sm font-medium hover:bg-primary/5 disabled:opacity-50 ml-auto">
                    {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />}
                    Re-run AI
                  </button>
                )}
              </div>
            )}
            {aiError && !aiLoading && (
              <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                AI suggestion unavailable: {aiError} You can still screen this paper yourself.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

// ─── Extraction queue ─────────────────────────────────────────────────────────

interface ExtractionQueueStudy {
  id: string;
  title: string;
  authors: string[];
  year: number | null;
  journal: string | null;
  doi: string | null;
  fullTextPath: string | null;
  status: string;
  extraction: {
    isConfirmed: boolean;
    isAiSuggested: boolean;
    updatedAt: string;
    extractedBy: { id: string; name: string } | null;
  } | null;
  appraisal: { isConfirmed: boolean; isAiSuggested: boolean; tool: string } | null;
}

function ExtractionQueuePanel({ reviewId, stage }: { reviewId: string; stage: "extraction" | "completed" }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  useEffect(() => { setPage(1); }, [reviewId, stage]);

  const { data, isLoading } = useQuery({
    queryKey: ["extraction-queue", reviewId, stage, page],
    queryFn: () =>
      api.get<{ studies: ExtractionQueueStudy[]; total: number; totalPages: number }>(
        `/screening/extraction-queue?reviewId=${reviewId}&stage=${stage}&page=${page}&pageSize=${PAGE_SIZE}`
      ),
    enabled: !!reviewId,
  });

  const studies = data?.studies ?? [];
  useEffect(() => {
    if (data && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
        <Loader2 className="h-5 w-5 animate-spin" /> {stage === "completed" ? "Loading completed papers…" : "Loading extraction queue…"}
      </div>
    );
  }

  if (studies.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground border rounded-lg bg-card">
        <ClipboardList className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
        <p className="font-semibold text-base text-foreground">
          {stage === "completed" ? "No completed papers yet" : "No studies ready for extraction"}
        </p>
        <p className="text-sm mt-2 max-w-sm mx-auto">
          {stage === "completed"
            ? "A paper moves here once both its data extraction and its quality appraisal are confirmed for this review."
            : "Studies appear here once they pass full-text screening for this review. Once both the extraction and the quality appraisal are confirmed, a paper moves to Completed."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Pagination page={page} pageSize={PAGE_SIZE} total={data?.total ?? 0} noun={stage === "completed" ? "completed" : "in extraction"} onPageChange={setPage} />
      {studies.map((study) => {
        const ex = study.extraction;
        const ap = study.appraisal;
        const appraisalBadge = ap?.isConfirmed ? (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-medium">
            <ShieldCheck className="h-3 w-3" /> Appraisal confirmed
          </span>
        ) : ap ? (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-medium">
            <ShieldCheck className="h-3 w-3" /> Appraisal draft{ap.isAiSuggested ? " (AI)" : ""}
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[11px] font-medium">
            Appraisal not started
          </span>
        );
        const statusBadge = ex?.isConfirmed ? (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-medium">
            <CheckCircle2 className="h-3 w-3" /> Extraction confirmed
          </span>
        ) : ex ? (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-medium">
            <Bot className="h-3 w-3" /> Draft{ex.isAiSuggested ? " (AI)" : ""}
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[11px] font-medium">
            Not started
          </span>
        );
        return (
          <div key={study.id} className="border rounded-lg p-3 bg-card flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium line-clamp-2">{study.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {study.authors.slice(0, 2).join(", ")}
                {study.authors.length > 2 ? " et al." : ""}
                {study.year ? ` · ${study.year}` : ""}
                {study.journal ? ` · ${study.journal}` : ""}
              </p>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {statusBadge}
                {appraisalBadge}
                {!study.fullTextPath && (
                  <span className="text-[11px] text-amber-600" title="No PDF uploaded; extraction will use the abstract only">
                    no PDF
                  </span>
                )}
                {ex?.extractedBy && (
                  <span className="text-[11px] text-muted-foreground">by {ex.extractedBy.name}</span>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              <button
                onClick={() => navigate(`/extraction/${study.id}/${reviewId}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-magenta text-white rounded-md text-xs font-medium hover:opacity-90"
              >
                <ClipboardList className="h-3.5 w-3.5" /> Extract data
              </button>
              <button
                onClick={() => navigate(`/risk-of-bias/${study.id}/${reviewId}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-xs hover:bg-accent"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Quality appraisal
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PhaseStep({ label, active }: { label: string; active: boolean }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
      {label}
    </span>
  );
}

function DecisionBadge({ decision }: { decision: "INCLUDE" | "EXCLUDE" | "UNCERTAIN" }) {
  const map = {
    INCLUDE: "bg-green-100 text-green-800",
    EXCLUDE: "bg-red-100 text-red-800",
    UNCERTAIN: "bg-yellow-100 text-yellow-800",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[decision]}`}>{decision}</span>;
}

/** Renders the structured criteria/reasons checked on a decision, if any. */
function CriteriaBadges({ codes }: { codes?: string[] }) {
  if (!codes || codes.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {codes.map((code) => (
        <span key={code} className="px-1.5 py-0.5 rounded bg-secondary border text-[10px] text-muted-foreground">
          {code}
        </span>
      ))}
    </div>
  );
}

function TextSourceBadge({ textSource, phase }: { textSource: "full_text" | "abstract" | "title_only" | "scanned_pdf" | undefined; phase: "TITLE_ABSTRACT" | "FULL_TEXT" }) {
  if (!textSource) return null;
  const configs = {
    full_text: { label: "Based on full text", className: "bg-green-100 text-green-800 border-green-200" },
    abstract: {
      label: phase === "FULL_TEXT" ? "Based on abstract only (no PDF)" : "Based on abstract",
      className: phase === "FULL_TEXT" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-blue-100 text-blue-800 border-blue-200",
    },
    title_only: { label: "Based on title only (no abstract)", className: "bg-orange-100 text-orange-800 border-orange-200" },
    // The PDF is a scan with no text layer, so the AI could only read the abstract.
    scanned_pdf: { label: "Scanned PDF, no readable text: based on abstract", className: "bg-amber-100 text-amber-800 border-amber-200" },
  };
  const { label, className } = configs[textSource];
  return <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${className}`}>{label}</span>;
}

function CharCoverageBadge({ charsAvailable, charsUsed, aiMaxChars }: { charsAvailable: number; charsUsed: number; aiMaxChars: number }) {
  const pct = charsAvailable > 0 ? Math.round((charsUsed / charsAvailable) * 100) : 100;
  const truncated = charsUsed < charsAvailable;
  const fmt = (n: number) => n.toLocaleString();
  if (!truncated) return <p className="text-xs text-muted-foreground">Full paper text used ({fmt(charsUsed)} chars)</p>;
  return (
    <div className="text-xs space-y-0.5">
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-secondary rounded-full h-1.5 overflow-hidden">
          <div className="bg-amber-400 h-full rounded-full" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-muted-foreground whitespace-nowrap">{fmt(charsUsed)} / {fmt(charsAvailable)} chars ({pct}% of paper)</span>
      </div>
      <p className="text-amber-700 dark:text-amber-400">
        ⚠ Paper was truncated to {fmt(aiMaxChars)} chars. {fmt(charsAvailable - charsUsed)} chars ({100 - pct}%) not seen by AI.
        Increase the AI character limit in Admin → Reviews if needed.
      </p>
    </div>
  );
}

interface PromptRecord {
  prompt: { system: string; user: string } | null;
  modelId: string | null;
  usage?: { inputTokens: number; outputTokens: number } | null;
  createdAt: string | null;
}

/**
 * Icon next to "AI suggestion": hover (or click, on touch screens) to see the
 * exact prompt that produced the suggestion, with a copy button. Loaded on
 * demand because a full-text prompt can carry tens of thousands of characters.
 */
function PromptPeek({ studyId, reviewId, phase, version = "" }: {
  studyId: string;
  reviewId: string;
  phase: "TITLE_ABSTRACT" | "FULL_TEXT";
  version?: string;
}) {
  const costDisplay = useCostDisplay();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [record, setRecord] = useState<PromptRecord | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setRecord(null); setLoadError(null); }, [studyId, reviewId, phase, version]);

  useEffect(() => {
    if (!open || record) return;
    api.get<PromptRecord>(`/screening/ai-prompt?studyId=${studyId}&reviewId=${reviewId}&phase=${phase}`)
      .then(setRecord)
      .catch((err: unknown) => setLoadError(err instanceof Error ? err.message : "Could not load the prompt."));
  }, [open, record, studyId, reviewId, phase]);

  const show = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
  // Short delay so the pointer can travel from the icon into the panel.
  const hide = () => { if (!pinned) closeTimer.current = setTimeout(() => setOpen(false), 200); };

  const fullText = record?.prompt ? `SYSTEM PROMPT\n\n${record.prompt.system}\n\nUSER MESSAGE\n\n${record.prompt.user}` : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked: the text is still selectable */ }
  };

  const usageLine = formatUsageWithCost(record?.usage, costDisplay);
  return (
    <span className="relative inline-flex" onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        onClick={() => { setPinned((p) => !p); setOpen(true); }}
        className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
        aria-label="Show the prompt sent to the AI"
        title="Show the prompt sent to the AI"
      >
        <MessageSquareCode className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-6 z-30 w-[36rem] max-w-[85vw] rounded-md border bg-background shadow-lg text-left">
          <div className="flex items-center gap-2 px-3 py-2 border-b">
            <span className="text-xs font-medium">Prompt sent to the AI</span>
            {record?.modelId && <span className="text-[11px] text-muted-foreground">{record.modelId}</span>}
            {record?.createdAt && <span className="text-[11px] text-muted-foreground">{new Date(record.createdAt).toLocaleString()}</span>}
            {usageLine && <span className="text-[11px] text-muted-foreground">{usageLine}</span>}
            <div className="ml-auto flex items-center gap-1">
              {record?.prompt && (
                <button type="button" onClick={copy} className="flex items-center gap-1 px-2 py-0.5 rounded border text-xs hover:bg-accent">
                  {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              )}
              {pinned && (
                <button type="button" onClick={() => { setPinned(false); setOpen(false); }} className="p-0.5 rounded hover:bg-accent" aria-label="Close">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-96 overflow-auto px-3 py-2">
            {loadError ? (
              <p className="text-xs text-destructive">{loadError}</p>
            ) : !record ? (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading prompt</p>
            ) : !record.prompt ? (
              <p className="text-xs text-muted-foreground">
                This suggestion was made before prompts were recorded, so its prompt is not available. Re-running the AI will record one.
              </p>
            ) : (
              <div className="space-y-3">
                <section>
                  <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">System prompt</h4>
                  <pre className="whitespace-pre-wrap break-words text-[11px] leading-snug font-mono">{record.prompt.system}</pre>
                </section>
                <section>
                  <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">User message</h4>
                  <pre className="whitespace-pre-wrap break-words text-[11px] leading-snug font-mono">{record.prompt.user}</pre>
                </section>
              </div>
            )}
          </div>
        </div>
      )}
    </span>
  );
}

const VERDICT_STYLES = {
  good: "bg-green-100 text-green-800 border-green-200",
  bad: "bg-red-100 text-red-800 border-red-200",
  unclear: "bg-amber-100 text-amber-800 border-amber-200",
};

function VerdictPill({ kind, verdict }: { kind: CriterionRow["kind"]; verdict: Verdict | null }) {
  if (verdict === null) return <span className="text-xs text-muted-foreground" title="Not assessed by the AI">-</span>;
  const [label, tone] =
    verdict === "UNCLEAR" ? ["Unclear", "unclear"] as const
    : kind === "INCLUSION" ? (verdict === "YES" ? ["Met", "good"] as const : ["Not met", "bad"] as const)
    : verdict === "YES" ? ["Applies", "bad"] as const : ["Does not apply", "good"] as const;
  return <span className={`inline-block whitespace-nowrap text-[11px] px-1.5 py-0.5 rounded border font-medium ${VERDICT_STYLES[tone]}`}>{label}</span>;
}

// Before a choice: three buttons whose meaning is about the criterion itself
// (tick = met / applies, cross = not met / does not apply, ? = unclear), never
// "agree with the AI". After a choice: the same pill wording and colours as
// the AI column, so the two can be compared at a glance, plus a change icon.
function MarkButtons({ row, value, onMark }: { row: CriterionRow; value: Verdict | undefined; onMark: (id: string, v: Verdict | null) => void }) {
  if (value) {
    return (
      <div className="flex items-center gap-1 justify-center">
        <VerdictPill kind={row.kind} verdict={value} />
        <button type="button" title="Change your answer" aria-label="Change your answer"
          onClick={() => onMark(row.id, null)}
          className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent">
          <RotateCcw className="h-3 w-3" />
        </button>
      </div>
    );
  }
  const options: Array<{ v: Verdict; label: string; icon: typeof Check; hover: string }> = [
    { v: "YES", label: row.kind === "INCLUSION" ? "Met" : "Applies", icon: Check,
      hover: row.kind === "INCLUSION" ? "hover:bg-green-100 hover:text-green-800" : "hover:bg-red-100 hover:text-red-800" },
    { v: "NO", label: row.kind === "INCLUSION" ? "Not met" : "Does not apply", icon: X,
      hover: row.kind === "INCLUSION" ? "hover:bg-red-100 hover:text-red-800" : "hover:bg-green-100 hover:text-green-800" },
    { v: "UNCLEAR", label: "Unclear", icon: HelpCircle, hover: "hover:bg-amber-100 hover:text-amber-800" },
  ];
  return (
    <div className="flex gap-1 justify-center">
      {options.map(({ v, label, icon: Icon, hover }) => (
        <button key={v} type="button" title={label} aria-label={label}
          onClick={() => onMark(row.id, v)}
          className={`p-1 rounded border bg-background transition-colors ${hover}`}>
          <Icon className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );
}

function CriteriaRows({ rows, marks, onMark }: {
  rows: CriterionRow[];
  marks?: Record<string, Verdict>;
  onMark?: (id: string, v: Verdict | null) => void;
}) {
  return (
    <>
      {rows.map((row) => {
        const mark = marks?.[row.id];
        const agrees = mark !== undefined && row.verdict !== null && mark === row.verdict;
        // Only a definite answer against a definite AI verdict counts as a disagreement.
        const differs = mark !== undefined && row.verdict !== null && !agrees && mark !== "UNCLEAR" && row.verdict !== "UNCLEAR";
        return (
          <tr key={row.id} className={`border-t align-top ${differs ? "bg-amber-50" : agrees ? "bg-green-50/60" : ""}`}>
            <td className="py-1.5 pr-2">
              {/* The review's own wording, exactly as written in Admin, Reviews.
                  An earlier version showed an AI-shortened label here instead. */}
              <span className="font-medium">{row.criterion}</span>
              {row.note && <span className="block text-muted-foreground">{row.note}</span>}
              {differs && <span className="block text-amber-700 font-medium">You differ from the AI here</span>}
            </td>
            <td className="py-1.5 px-2 text-center"><VerdictPill kind={row.kind} verdict={row.verdict} /></td>
            {onMark && <td className="py-1.5 pl-2"><MarkButtons row={row} value={mark} onMark={onMark} /></td>}
          </tr>
        );
      })}
    </>
  );
}

function CriteriaTable({ criteria, marks, onMark, requireAll = false }: {
  criteria: CriterionRow[];
  marks?: Record<string, Verdict>;
  onMark?: (id: string, v: Verdict | null) => void;
  requireAll?: boolean; // full text: every criterion must be marked, so none are hidden
}) {
  const [showAllExclusionsToggle, setShowAllExclusions] = useState(false);
  const showAllExclusions = requireAll || showAllExclusionsToggle;
  const inclusion = criteria.filter((c) => c.kind === "INCLUSION");
  const exclusion = criteria.filter((c) => c.kind === "EXCLUSION");
  // Exclusion criteria are often the mirror image of inclusion ones, so only
  // those the AI thinks apply (or cannot tell) are shown by default.
  const flagged = exclusion.filter((c) => c.verdict !== "NO");
  const visibleExclusions = showAllExclusions ? exclusion : flagged;
  const hiddenCount = exclusion.length - visibleExclusions.length;

  const marked = criteria.filter((c) => marks?.[c.id] && marks[c.id] !== "UNCLEAR" && c.verdict !== null && c.verdict !== "UNCLEAR");
  const unmarked = criteria.filter((c) => !marks?.[c.id]).length;
  const agreeing = marked.filter((c) => marks![c.id] === c.verdict).length;
  const head = "py-1 font-medium text-muted-foreground";

  return (
    <div className="space-y-1.5">
      <table className="w-full text-xs">
        <thead>
          <tr>
            <th className={`${head} text-left`}>Inclusion criteria</th>
            <th className={`${head} px-2 w-24`}>AI</th>
            {onMark && <th className={`${head} pl-2 w-36`} title="Your own judgement of each criterion: tick = met (or applies), cross = not met (or does not apply), question mark = unclear">You</th>}
          </tr>
        </thead>
        <tbody>
          <CriteriaRows rows={inclusion} marks={marks} onMark={onMark} />
          {exclusion.length > 0 && (
            <tr className="border-t">
              <th className={`${head} text-left pt-3`} colSpan={onMark ? 3 : 2}>
                Exclusion criteria
                {flagged.length === 0 && !showAllExclusions && (
                  <span className="font-normal">: the AI found none that apply ({exclusion.length} checked).</span>
                )}
              </th>
            </tr>
          )}
          <CriteriaRows rows={visibleExclusions} marks={marks} onMark={onMark} />
        </tbody>
      </table>
      {!requireAll && (hiddenCount > 0 || showAllExclusions) && exclusion.length > flagged.length && (
        <button type="button" onClick={() => setShowAllExclusions((v) => !v)} className="text-xs text-primary hover:underline">
          {showAllExclusions ? "Show only flagged exclusion criteria" : `Show all ${exclusion.length} exclusion criteria`}
        </button>
      )}
      {onMark && requireAll && unmarked > 0 && (
        <p className="text-xs text-amber-700 font-medium">
          At full text every criterion must be marked: {unmarked} still to do.
        </p>
      )}
      {onMark && marked.length > 0 && (
        <p className="text-xs text-muted-foreground">
          You marked {marked.length} {marked.length === 1 ? "criterion" : "criteria"} the AI gave a verdict on: you agree on {agreeing}
          {marked.length - agreeing > 0 ? ` and differ on ${marked.length - agreeing}` : ""}.
        </p>
      )}
    </div>
  );
}

function AiSuggestionBody({ view, marks, onMark, compact = false, criteria, requireAll = false }: {
  view: AiView;
  marks?: Record<string, Verdict>;
  onMark?: (id: string, v: Verdict | null) => void;
  compact?: boolean; // read-only context card: highlights and summary only
  criteria?: CriterionRow[]; // review criteria merged with the AI verdicts; defaults to the AI rows
  requireAll?: boolean;
}) {
  const rows = criteria ?? view.criteria;
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-3">
        <DecisionBadge decision={view.decision} />
        {view.confidence != null && (
          <span className="text-xs text-muted-foreground">Confidence: {Math.round(view.confidence * 100)}%</span>
        )}
      </div>
      {view.highlights.length > 0 && (
        <ul className="text-xs text-foreground/80 list-disc pl-4 space-y-0.5">
          {view.highlights.map((h, i) => <li key={i}>{h}</li>)}
        </ul>
      )}
      {!compact && rows.length > 0 && <CriteriaTable criteria={rows} marks={marks} onMark={onMark} requireAll={requireAll} />}
      {view.summary && (
        <p className="text-xs text-muted-foreground leading-relaxed">
          <span className="font-medium text-foreground">AI summary: </span>{view.summary}
        </p>
      )}
    </div>
  );
}
